import { useState } from "react";
import { useParams } from "react-router";
import { Link, useOutletContext } from "react-router-dom";

import {
  Paper,
  Stack,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Box,
} from "@mui/material";

import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";

import Loader from "../../components/Loader";
import CompareDialog from "../../components/images/CompareDialog";
import EcrfBlocks from "../../components/Trials/EcrfBlocks";
import useGetExternalMediaGallery from "../../hooks/useGetExternalMediaGallery";
import { INV_RELATED_ROLES, USER_ROLES } from "../../utils/constants";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const ExternalMediaGalleryView = ({ mediaType = "photo" }) => {
  const { _id_ } = useParams();
  const role = JSON.parse(localStorage.getItem("user_data"))?.role;

  const isPhoto = mediaType === "photo";
  const activeKey = isPhoto ? "externalPhotos" : "externalVideos";
  const label = isPhoto ? "Photos" : "Videos";
  const routeSegment = isPhoto ? "sessions_external_photos" : "sessions_external_videos";

  const { setActive, setBreadcrumbTitle, trialFilter, isTrialLocked } = useOutletContext();
  setActive(activeKey);
  setBreadcrumbTitle(`External ${label} - Gallery View`);

  const [compareOpen, setCompareOpen] = useState(false);
  const [selectedCompareImage, setSelectedCompareImage] = useState(null);
  const [scoreContext, setScoreContext] = useState(null);

  const canScore =
    !isTrialLocked && (INV_RELATED_ROLES.includes(role) || role === USER_ROLES.REVIEWER);

  const { data: externalMedia = [], isLoading: loading } = useGetExternalMediaGallery({
    patientId: _id_,
    mediaType,
  });

  const renderDate = (dt) => {
    if (!dt) return "-";

    return dayjs.utc(dt).local().format("YYYY-MM-DD HH:mm");
  };

  const handleOnImageClick = (img) => {
    setCompareOpen(true);
    setSelectedCompareImage(img);
  };

  const items = externalMedia.flatMap((s) =>
    (s[isPhoto ? "external_session_media_photo" : "external_session_media_video"] || []).map(
      (item) => ({ ...item, session: s })
    )
  );

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">External {label} – Gallery View</Typography>

        <Stack direction="row" spacing={1}>
          <Button
            component={Link}
            to={`/patient/${_id_}/${routeSegment}?trial=${trialFilter}`}
            variant="outlined"
            size="small"
          >
            Session View
          </Button>
          <Button
            component={Link}
            to={`/patient/${_id_}/${routeSegment}_gallery?trial=${trialFilter}`}
            variant="contained"
            size="small"
          >
            Gallery View
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Stack alignItems="center" py={4}>
          <Loader />
        </Stack>
      ) : items.length === 0 ? (
        <Typography color="text.secondary" align="center">
          No external {label.toLowerCase()} available
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {items.map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item.id}>
              <Card variant="outlined">
                {isPhoto ? (
                  <CardMedia
                    component="img"
                    height="180"
                    image={item.image}
                    onClick={() => handleOnImageClick(item.image)}
                    sx={{ cursor: "pointer" }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 180,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "black",
                    }}
                  >
                    <video
                      src={item.video}
                      playsInline
                      controls
                      style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "cover" }}
                    />
                  </Box>
                )}

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {(isPhoto ? item?.photo_type_name : item?.video_type_name) || "-"}
                  </Typography>

                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Visit Name:</strong> {item?.visit_number || "-"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Captured At:</strong> {renderDate(item?.capture_datetime)}
                    </Typography>
                  </Stack>
                </CardContent>

                {isPhoto && item.session?.external_score_ecrf_name && (
                  <Box sx={{ px: 1, pb: 1.5 }}>
                    <Button
                      fullWidth
                      data-testid="external-score-ecrf-btn"
                      variant="contained"
                      startIcon={<AssignmentTurnedInOutlinedIcon />}
                      disabled={!canScore}
                      onClick={() =>
                        setScoreContext({
                          visit_number: item.session.ecrf_visit_number,
                          visitName: item.session.visit_name,
                          trialId: item.session.trial_id,
                          trialName: item.session.trial_name,
                          selectedBlock: item.session.external_score_ecrf_name,
                          selectedSessionId: item.session.photo_session,
                        })
                      }
                    >
                      Score in eCRF
                    </Button>
                  </Box>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {isPhoto && (
        <CompareDialog
          open={compareOpen}
          images={items}
          onClose={() => setCompareOpen(false)}
          selectedImage={selectedCompareImage}
        />
      )}

      {scoreContext && (
        <EcrfBlocks
          visit_number={scoreContext.visit_number}
          visitName={scoreContext.visitName}
          trialId={scoreContext.trialId}
          trialName={scoreContext.trialName}
          selectedBlock={scoreContext.selectedBlock}
          fromQueryTable={true}
          useCompactModal={true}
          showEcrfBlock={Boolean(scoreContext)}
          setShowEcrfBlock={() => setScoreContext(null)}
          patientId={_id_}
          isTrialLocked={isTrialLocked}
          selectedSessionId={scoreContext.selectedSessionId}
        />
      )}
    </Paper>
  );
};

export default ExternalMediaGalleryView;
